import { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem } from './Breadcrumb';

interface PageHeaderProps {
  /** When `minimal`, only the breadcrumb row + tabs render (no icon/H1/subtitle/mb-6). */
  variant?: 'default' | 'minimal';
  title?: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  breadcrumb?: BreadcrumbItem[];
  /** Optional page icon (template: icon + title block). */
  icon?: LucideIcon;
  /**
   * `below` (default): icon + title + subtitle, then breadcrumb row (list/detail template).
   * `above`: legacy — breadcrumb → title → subtitle.
   */
  breadcrumbPosition?: 'above' | 'below';
  /** Shown after breadcrumb when `breadcrumbPosition` is `below` (e.g. Data entity tabs). */
  tabs?: ReactNode;
  /** Right-aligned content on the same row as the breadcrumb (e.g. Operations menu). */
  breadcrumbRowEnd?: ReactNode;
  /** Extra classes on the root wrapper (default includes `mb-6`). */
  className?: string;
  /** Extra classes on the top row (icon + title + action). Replaces default `vscode-border` on the bottom edge when set. */
  headerBarClassName?: string;
}

export function PageHeader({
  variant = 'default',
  title,
  subtitle,
  action,
  breadcrumb,
  icon: Icon,
  breadcrumbPosition = 'below',
  tabs,
  breadcrumbRowEnd,
  className,
  headerBarClassName,
}: PageHeaderProps) {
  const breadcrumbEl =
    breadcrumb && breadcrumb.length > 0 ? <Breadcrumb items={breadcrumb} /> : null;

  if (variant === 'minimal') {
    // The shell Topbar already renders the active breadcrumb, so repeating it
    // inside the minimal PageHeader produced the duplicate-title regression the
    // user flagged. We now ignore `breadcrumb` in minimal mode and only render
    // `breadcrumbRowEnd` (right-aligned controls) + `tabs`. Non-minimal pages
    // keep the H1 + breadcrumb block because their detail templates rely on it.
    return (
      <div className={[className].filter(Boolean).join(' ')}>
        {breadcrumbRowEnd ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {breadcrumbRowEnd}
          </div>
        ) : null}
        {tabs ? <div className={breadcrumbRowEnd ? 'mt-2' : ''}>{tabs}</div> : null}
      </div>
    );
  }

  return (
    <div className={['mb-6', className].filter(Boolean).join(' ')}>
      <div
        className={[
          'ggj-page-header-bar flex items-start justify-between gap-4 pb-4 border-b',
          headerBarClassName ?? 'vscode-border',
        ].join(' ')}
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {Icon ? (
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--brand-accent-bg)', border: '1px solid var(--brand-accent-border)' }}
              aria-hidden
            >
              <Icon className="w-5 h-5" style={{ color: 'var(--vscode-primary)' }} />
            </div>
          ) : null}
          <div className="min-w-0">
            {breadcrumbPosition === 'above' && breadcrumbEl}
            <h1 className="gg-app-page-title">
              {title ?? ''}
            </h1>
            {subtitle != null && subtitle !== '' && <div className="gg-app-page-subtitle">{subtitle}</div>}
          </div>
        </div>
        {action && <div className="flex-shrink-0 flex items-center gap-2">{action}</div>}
      </div>
      {breadcrumbPosition === 'below' && (
        <>
          {(breadcrumbEl || breadcrumbRowEnd) && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 gap-y-2">
              <div className="min-w-0 flex-1">{breadcrumbEl}</div>
              {breadcrumbRowEnd ? (
                <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 ml-auto">{breadcrumbRowEnd}</div>
              ) : null}
            </div>
          )}
          {tabs ? <div className={breadcrumbEl || breadcrumbRowEnd ? 'mt-2' : 'mt-3'}>{tabs}</div> : null}
        </>
      )}
    </div>
  );
}
